/**
 * Types for Transit Stops (Frontend)
 *
 * @remarks
 * Define los tipos TypeScript para paradas de transporte público.
 * Estos tipos deben coincidir con las entidades del backend.
 *
 * @module Mobility/Stop
 */

import type { Point } from 'geojson';
import type { NetworkAssociation } from '../../../spatial';

/**
 * Tipo de parada de transporte público
 */
export type TransitStopType = 'bus' | 'metro' | 'tram' | 'ferry';

/**
 * Metadata adicional de la parada
 */
export interface TransitStopMetadata {
  /**
   * Rutas que pasan por esta parada
   */
  routes?: string[];

  /**
   * Horarios de operación
   */
  schedule?: {
    weekday?: string;
    weekend?: string;
  };

  /**
   * Amenities disponibles en la parada
   */
  amenities?: string[];
}

/**
 * TransitStop - Representa una parada de transporte público
 *
 * @remarks
 * Esta interface representa el modelo completo de una parada
 * tal como es retornado por el backend.
 */
export interface TransitStop {
  /**
   * ID único de la parada (UUID v4)
   */
  id: string;

  /**
   * ID del tenant propietario
   */
  tenantId: string;

  /**
   * ID del proyecto al que pertenece
   */
  projectId: string;

  /**
   * Nombre de la parada
   */
  name: string;

  /**
   * Código único de la parada (por proyecto)
   */
  code: string;

  /**
   * Tipo de parada
   */
  type: TransitStopType;

  /**
   * Geometría de la parada (GeoJSON Point)
   */
  geometry: Point;

  /**
   * Indica si la parada es accesible (rampa, braille, etc.)
   */
  accessible: boolean;

  /**
   * Metadata adicional (opcional)
   */
  metadata?: TransitStopMetadata | null;

  /**
   * Asociación con la red canónica (edge o node)
   *
   * @remarks
   * Contiene información sobre a qué elemento de la red está asociada
   * esta parada, incluyendo posición sobre el edge, distancia, lado, etc.
   */
  networkAssociation?: NetworkAssociation | null;

  /**
   * ID del usuario que creó la parada
   */
  createdBy: string;

  /**
   * Fecha de creación
   */
  createdAt: string;

  /**
   * Fecha de última actualización
   */
  updatedAt: string;
}

/**
 * DTO para crear una nueva parada
 */
export interface CreateStopDto {
  /**
   * Nombre de la parada
   */
  name: string;

  /**
   * Código único de la parada
   */
  code: string;

  /**
   * Tipo de parada
   */
  type: TransitStopType;

  /**
   * Geometría de la parada (GeoJSON Point)
   */
  geometry: Point;

  /**
   * ID del proyecto
   */
  projectId: string;

  /**
   * Indica si es accesible
   */
  accessible?: boolean;

  /**
   * Metadata adicional (opcional)
   */
  metadata?: TransitStopMetadata;

  /**
   * Asociación con la red canónica (opcional, se calcula automáticamente si no se provee)
   */
  networkAssociation?: NetworkAssociation;
}

/**
 * DTO para actualizar una parada existente
 */
export interface UpdateStopDto {
  /**
   * Nombre de la parada (opcional)
   */
  name?: string;

  /**
   * Código de la parada (opcional)
   */
  code?: string;

  /**
   * Tipo de parada (opcional)
   */
  type?: TransitStopType;

  /**
   * Geometría de la parada (opcional)
   */
  geometry?: Point;

  /**
   * Indica si es accesible (opcional)
   */
  accessible?: boolean;

  /**
   * Metadata adicional (opcional)
   */
  metadata?: TransitStopMetadata;

  /**
   * Asociación con la red canónica (opcional)
   */
  networkAssociation?: NetworkAssociation | null;
}

/**
 * Filtros para búsqueda de paradas
 */
export interface StopFilters {
  /**
   * ID del proyecto
   */
  projectId?: string;

  /**
   * Tipo de parada
   */
  type?: TransitStopType;

  /**
   * Solo paradas accesibles
   */
  accessible?: boolean;

  /**
   * Búsqueda por nombre o código
   */
  search?: string;

  /**
   * Bounding box para búsqueda espacial
   */
  bounds?: {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
  };
}

/**
 * Opciones de formato para labels de paradas
 */
export interface StopLabelOptions {
  /**
   * Incluir código en el label
   */
  includeCode?: boolean;

  /**
   * Incluir tipo en el label
   */
  includeType?: boolean;

  /**
   * Incluir emoji según tipo
   */
  includeEmoji?: boolean;
}

/**
 * Helper para generar label de una parada
 *
 * @param stop - Parada
 * @param options - Opciones de formato
 * @returns Label formateado
 *
 * @example
 * ```typescript
 * getStopLabel(stop); // "Parada Brown y Chiclana"
 * getStopLabel(stop, { includeCode: true }); // "BB-001: Parada Brown y Chiclana"
 * getStopLabel(stop, { includeType: true, includeEmoji: true }); // "🚏 Parada Brown y Chiclana (bus)"
 * ```
 */
export function getStopLabel(stop: TransitStop, options?: StopLabelOptions): string {
  const { includeCode = false, includeType = false, includeEmoji = false } = options || {};

  let label = '';

  // Emoji según tipo
  if (includeEmoji) {
    const emoji = getStopTypeEmoji(stop.type);
    label += `${emoji} `;
  }

  // Código
  if (includeCode) {
    label += `${stop.code}: `;
  }

  // Nombre
  label += stop.name;

  // Tipo
  if (includeType) {
    label += ` (${stop.type})`;
  }

  return label;
}

/**
 * Helper para obtener emoji según tipo de parada
 *
 * @param type - Tipo de parada
 * @returns Emoji correspondiente
 */
export function getStopTypeEmoji(type: TransitStopType): string {
  switch (type) {
    case 'bus':
      return '🚏';
    case 'metro':
      return '🚇';
    case 'tram':
      return '🚊';
    case 'ferry':
      return '⛴️';
    default:
      return '🚏';
  }
}

/**
 * Helper para obtener nombre legible del tipo de parada
 *
 * @param type - Tipo de parada
 * @returns Nombre en español
 */
export function getStopTypeName(type: TransitStopType): string {
  switch (type) {
    case 'bus':
      return 'Parada de Bus';
    case 'metro':
      return 'Estación de Metro';
    case 'tram':
      return 'Parada de Tranvía';
    case 'ferry':
      return 'Terminal de Ferry';
    default:
      return 'Parada';
  }
}
